import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function About() {
  const values = [
    { icon: 'ri-shield-check-line', title: 'Integrity', description: 'Honest and transparent in all our dealings', color: 'from-blue-400 to-blue-600' },
    { icon: 'ri-flashlight-line', title: 'Reliability', description: 'Consistent and dependable service delivery', color: 'from-orange-400 to-orange-600' },
    { icon: 'ri-line-chart-line', title: 'Innovation', description: 'Embracing technology to improve experiences', color: 'from-purple-400 to-purple-600' },
    { icon: 'ri-award-line', title: 'Efficiency', description: 'Streamlined operations for better service', color: 'from-green-400 to-green-600' },
    { icon: 'ri-heart-line', title: 'Customer Satisfaction', description: 'Putting customers at the heart of everything', color: 'from-red-400 to-red-600' },
    { icon: 'ri-group-line', title: 'Partnership & Growth', description: 'Building strong relationships with vendors', color: 'from-teal-400 to-teal-600' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <div className="pt-20">
      <SEO 
        title="About Ami Bites - Zimbabwe's Premier Food Delivery Platform | Pitch For Me Group"
        description="Learn about Ami Bites, Zimbabwe's leading technology-driven food ordering and delivery platform. Part of Pitch For Me Group, we deliver excellence through integrity, reliability, and innovation."
        keywords="about Ami Bites, food delivery company Zimbabwe, Pitch For Me Group, Zimbabwe food tech, online ordering platform, corporate food delivery, restaurant partnerships Zimbabwe"
        canonical="https://amibites.com/about"
      />
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 to-green-400 rounded-3xl opacity-20 blur-2xl"></div>
                <img
                  src="/logo.png"
                  alt="Ami Bites"
                  className="relative rounded-3xl shadow-2xl w-full"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-6">
                Who <span className="text-gradient">We Are</span>
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Ami Bites is a technology-driven online food ordering and delivery platform designed to simplify how individuals and organizations access quality meals.
                </p>
                <p>
                  We provide a structured digital marketplace that connects customers with trusted restaurants and food vendors while ensuring reliable logistics and professional service standards.
                </p>
                <div className="mt-8 p-6 bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl border-l-4 border-orange-600">
                  <h3 className="font-bold text-xl mb-2 text-gradient">Our Corporate Foundation</h3>
                  <p className="text-gray-700">
                    Ami Bites is a proud member of the <strong>Pitch For Me Group of Companies</strong>, a diversified and innovation-focused business group committed to delivering excellence across multiple industries.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl relative overflow-hidden group hover:shadow-2xl transition-shadow"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl mb-6"
                >
                  <i className="ri-focus-3-line text-3xl text-white"></i>
                </motion.div>
                <h3 className="text-3xl font-bold mb-4 text-gradient">Our Vision</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  To become Zimbabwe's leading corporate-standard food ordering and delivery platform, recognized for reliability, professionalism, and service excellence.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-xl relative overflow-hidden group hover:shadow-2xl transition-shadow"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-green-400 to-green-600 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl mb-6"
                >
                  <i className="ri-compass-3-line text-3xl text-white"></i>
                </motion.div>
                <h3 className="text-3xl font-bold mb-4 text-gradient-green">Our Mission</h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  To deliver a secure, efficient, and customer-centric food ordering and delivery solution that supports both consumers and food vendors.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gradient">Core Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all group"
              >
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl mb-6`}
                >
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </motion.div>
                <h3 className="text-2xl font-bold mb-3 text-gray-900">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

    </div>
  )
}
